// Redirect software blit/draw calls to GPU layers during dungeon render.

import type { Camera } from '../camera';
import type { Rect, Surface } from '../surface';
import {
  setGpuBlitHook,
  setGpuCircleHook,
  setGpuCircleExHook,
  setGpuRectHook,
} from '../surface';
import { getSurfacePath, getAssetScale } from '../assets';
import { RES_SCALE } from '../../core/constants';
import { drawIndexedSpriteDirect } from './sprites';
import { drawCircleGpu, drawCircleExGpu, drawFilledRectGpu } from './procedural';

const tr = Math.trunc;

let activeCam: Camera | null = null;

export function installGpuDrawBridge(cam: Camera): void {
  activeCam = cam;
  setGpuBlitHook(gpuBlit);
  setGpuRectHook(gpuRect);
  setGpuCircleHook(gpuCircle);
  setGpuCircleExHook(gpuCircleEx);
}

export function uninstallGpuDrawBridge(): void {
  activeCam = null;
  setGpuBlitHook(null);
  setGpuRectHook(null);
  setGpuCircleHook(null);
  setGpuCircleExHook(null);
}

function gpuBlit(
  src: Surface,
  srcRect: Rect | null,
  dx: number,
  dy: number,
  nativeScale: number
): void {
  if (!activeCam) return;
  const datPath = getSurfacePath(src);
  let sx = 0;
  let sy = 0;
  let sw = src.w;
  let sh = src.h;
  if (srcRect) {
    sx = tr(srcRect.x);
    sy = tr(srcRect.y);
    sw = tr(srcRect.w);
    sh = tr(srcRect.h);
  }
  const ns = datPath ? getAssetScale(datPath) : nativeScale;
  const scale = RES_SCALE / ns;
  const dw = sw * scale;
  const dh = sh * scale;
  if (!datPath) return;
  drawIndexedSpriteDirect(
    datPath,
    sx,
    sy,
    sw,
    sh,
    tr(dx),
    tr(dy),
    dw,
    dh
  );
}

function gpuRect(x: number, y: number, w: number, h: number, c: number): void {
  if (!activeCam) return;
  drawFilledRectGpu(x, y, w, h, c);
}

function gpuCircle(x: number, y: number, r: number, c: number): void {
  if (!activeCam) return;
  drawCircleGpu(x, y, r, c);
}

function gpuCircleEx(x: number, y: number, r: number, r2: number, c: number): void {
  if (!activeCam) return;
  drawCircleExGpu(x, y, r, r2, c);
}
